/**
 * ScormUploader — Upload SCORM zip packages with manifest preview.
 *
 * Features:
 *   - Drag & drop or file picker for .zip files
 *   - Upload progress indication
 *   - Parsed manifest preview (title, version, SCOs)
 *   - Replace / delete existing package
 *   - i18n via `language` prop
 */

import React, { useState, useCallback, useRef } from 'react';
import { bytesToMb, resolveLimitBytes, useUploadLimits } from '@ai-content-hub/shared-config';

import type { ScormUploaderProps, ScormUploadResult, ScormManifest } from './types';
import { uploadScormPackage, deleteScormPackage } from './api';
import { useScormTranslations } from './i18n';
import type { ScormTranslations } from './i18n';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

/** EAA-1212: the server's shipped SCORM cap, used until the configured one is known. */
const SCORM_FALLBACK_MAX_BYTES = 200 * 1024 * 1024;

export function ScormUploader({
    contentId,
    onUploadComplete,
    onUploadError,
    language,
    className,
}: ScormUploaderProps) {
    const [state, setState] = useState<UploadState>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [result, setResult] = useState<ScormUploadResult | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const t = useScormTranslations(language);
    const { scormMaxSizeMb } = useUploadLimits();
    const maxBytes = resolveLimitBytes(SCORM_FALLBACK_MAX_BYTES, scormMaxSizeMb);

    const handleFile = useCallback(
        async (file: File) => {
            if (!file.name.toLowerCase().endsWith('.zip')) {
                setErrorMsg(t.uploadOnlyZip);
                setState('error');
                return;
            }
            // EAA-1212: refuse an oversize package before sending it, using the
            // server-configured SCORM cap rather than letting a full upload fail.
            if (file.size > maxBytes) {
                setErrorMsg(t.uploadTooLarge.replace('{max}', String(bytesToMb(maxBytes))));
                setState('error');
                return;
            }

            setState('uploading');
            setErrorMsg('');

            try {
                const uploadResult = await uploadScormPackage(file, contentId);
                setResult(uploadResult);
                setState('success');
                onUploadComplete?.(uploadResult);
            } catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setErrorMsg(error.message);
                setState('error');
                onUploadError?.(error);
            }
        },
        [contentId, maxBytes, onUploadComplete, onUploadError, t],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) handleFile(file);
        },
        [handleFile],
    );

    const handleFileInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
        },
        [handleFile],
    );

    const handleReplace = useCallback(() => {
        setResult(null);
        setState('idle');
        setErrorMsg('');
    }, []);

    const handleDelete = useCallback(async () => {
        try {
            if (contentId) {
                await deleteScormPackage(contentId);
            }
            setResult(null);
            setState('idle');
        } catch (err) {
            setErrorMsg(t.uploadDeleteFailed);
            setState('error');
        }
    }, [contentId, t]);

    // Success view — manifest preview
    if (state === 'success' && result) {
        return (
            <div className={`scorm-uploader ${className ?? ''}`} style={styles.container}>
                <ManifestPreview result={result} t={t} />
                <div style={styles.actions}>
                    <button
                        type="button"
                        onClick={handleReplace}
                        style={styles.replaceBtn}
                    >
                        {t.uploadReplacePackage}
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        style={styles.deleteBtn}
                    >
                        {t.uploadDelete}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`scorm-uploader ${className ?? ''}`} style={styles.container}>
            <div
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                    ...styles.dropZone,
                    ...(isDragOver ? styles.dropZoneActive : {}),
                    ...(state === 'uploading' ? styles.dropZoneUploading : {}),
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleFileInput}
                    style={{ display: 'none' }}
                />

                {state === 'uploading' ? (
                    <div style={styles.uploadingContent}>
                        <div style={styles.spinner} />
                        <span style={styles.uploadingText}>{t.uploadUploading}</span>
                    </div>
                ) : (
                    <div style={styles.dropContent}>
                        <svg
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#9ca3af"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span style={styles.dropText}>
                            {t.uploadDropzone}
                        </span>
                        <span style={styles.dropHint}>
                            {t.uploadHint}
                        </span>
                    </div>
                )}
            </div>

            {state === 'error' && errorMsg && (
                <div style={styles.errorBox}>
                    {errorMsg}
                </div>
            )}
        </div>
    );
}

// ── Manifest Preview ────────────────────────────────────────────────────────

function ManifestPreview({ result, t }: { result: ScormUploadResult; t: ScormTranslations }) {
    const manifest = result.manifest;
    const scos = manifest?.scos ?? [];
    const fileSizeMB = (result.file_size / (1024 * 1024)).toFixed(1);

    return (
        <div style={styles.preview}>
            <div style={styles.previewHeader}>
                <div style={styles.previewIcon}>📦</div>
                <div>
                    <div style={styles.previewTitle}>
                        {manifest?.title ?? result.original_filename}
                    </div>
                    <div style={styles.previewMeta}>
                        {result.scorm_version === 'scorm-12' ? 'SCORM 1.2' : 'SCORM 2004'}
                        {' · '}
                        {fileSizeMB} MB
                        {' · '}
                        {result.original_filename}
                    </div>
                </div>
            </div>

            {scos.length > 0 && (
                <div style={styles.scoList}>
                    <div style={styles.scoListTitle}>
                        {t.manifestContentObjects} ({scos.length})
                    </div>
                    {scos.map((sco, i) => (
                        <div key={sco.identifier ?? i} style={styles.scoItem}>
                            <span style={styles.scoBadge}>{i + 1}</span>
                            <span>{sco.title}</span>
                        </div>
                    ))}
                </div>
            )}

            <div style={styles.launchInfo}>
                <span style={styles.launchLabel}>{t.manifestLaunchUrl}</span>
                <code style={styles.launchCode}>{result.launch_url}</code>
            </div>
        </div>
    );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
    container: {
        width: '100%',
    },
    dropZone: {
        border: '2px dashed #d1d5db',
        borderRadius: 12,
        padding: 32,
        textAlign: 'center' as const,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: '#fafafa',
    },
    dropZoneActive: {
        borderColor: '#3b82f6',
        backgroundColor: '#eff6ff',
    },
    dropZoneUploading: {
        cursor: 'wait',
        opacity: 0.7,
    },
    dropContent: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 8,
    },
    dropText: {
        fontSize: 14,
        fontWeight: 500,
        color: '#374151',
    },
    dropHint: {
        fontSize: 12,
        color: '#9ca3af',
    },
    uploadingContent: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 12,
    },
    spinner: {
        width: 32,
        height: 32,
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    uploadingText: {
        fontSize: 14,
        color: '#6b7280',
    },
    errorBox: {
        marginTop: 8,
        padding: '8px 12px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 8,
        color: '#dc2626',
        fontSize: 13,
    },
    preview: {
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 16,
        backgroundColor: '#ffffff',
    },
    previewHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    previewIcon: {
        fontSize: 28,
    },
    previewTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: '#111827',
    },
    previewMeta: {
        fontSize: 12,
        color: '#6b7280',
        marginTop: 2,
    },
    scoList: {
        marginTop: 12,
        padding: '8px 12px',
        backgroundColor: '#f9fafb',
        borderRadius: 8,
    },
    scoListTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: '#6b7280',
        marginBottom: 6,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
    },
    scoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 0',
        fontSize: 13,
        color: '#374151',
    },
    scoBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: '#dbeafe',
        color: '#2563eb',
        fontSize: 11,
        fontWeight: 600,
    },
    launchInfo: {
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
    },
    launchLabel: {
        color: '#6b7280',
        fontWeight: 500,
    },
    launchCode: {
        backgroundColor: '#f3f4f6',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: 11,
        color: '#374151',
        fontFamily: 'monospace',
    },
    actions: {
        display: 'flex',
        gap: 8,
        marginTop: 12,
    },
    replaceBtn: {
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
        border: '1px dashed #d1d5db',
        backgroundColor: 'transparent',
        color: '#374151',
        cursor: 'pointer',
    },
    deleteBtn: {
        padding: '6px 12px',
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
        border: '1px solid #fecaca',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        cursor: 'pointer',
    },
};
